import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Form, message, Tag } from 'antd'
import { castArray, cloneDeep, includes, isEmpty, toLower } from 'lodash-es'
import { SearchInput } from 'components/UI'
import AddFinanceTag from 'components/apps/FinanceTag/AddFinanceTag'
import { getTerm } from 'utils/hooks/useTerm'
import { checkContrast, cleanBlankValue, hexToRGB } from 'utils/helper'
import { useSelector } from 'react-redux'
import { termTitleSelector, websiteIdSelector } from 'app-redux/selectors'
import useMemoizePermissions from 'utils/hooks/useMemoizePermissions'
import useSuggestion from 'utils/hooks/useSuggestion'
import { useInfiniteTagSuggestion } from 'utils/queries'
import uuid from 'uuid'
import {
  getSpecialRegex,
  specialWebsiteIdForTagValidation,
} from 'components/apps/FinanceTag/FinanceTagForm'
import withAddTag from './withAddTag'
import styles from './tag-suggestion.module.scss'

// Identitas stabil supaya pemanggil level dokumen tidak membuat array baru tiap render.
const EMPTY_NAME_PREFIX = []

/**
 * @desc Tag suggestion component
 */
function TagSuggestion({
  params,
  rules = [],
  label,
  name,
  initialValue,
  defaultTag,
  styleFormItem,
  showTagModal,
  tagFormRef,
  loadingTag,
  onSubmitTag,
  onToggleTagModal,
  innerRef,
  isListField,
  fieldKey,
  accountConfig,
  className,
  tooltip,
  withTerm,
  onClear,
  confirmLoading,
  onSelect = () => {},
  onAfterSaved = () => {},
  asFilter = false,
  infiniteScroll = true,
  mode = 'multiple',
  execludeTags = [],
  allowClear = true,
  // dropdownStyle = { width: 400 },
  onResetSearch,
  addFinanceTag,
  getDefaultTags,
  formItemClassName,
  ownerOnly,
  onDeselect,
  formItemLoaderTag,
  namePrefix = EMPTY_NAME_PREFIX,
  withExtra = true,
  withLabel = true,
  disableSystemReservedTags = false,
  ...props
}) {
  const uniqueId = uuid()
  const { formatMessage } = useIntl()
  const [defaultTags, setDefaultTags] = useState([])
  const [newlyCreatedTag, setNewlyCreatedTag] = useState(null)

  // Mencegah form dari props yang masih null
  const form = Form.useFormInstance()
  // `name` bersifat relatif terhadap baris saat komponen dipakai di dalam `Form.List`,
  // sedangkan `form.setFieldValue`/`getFieldValue` selalu membaca path dari akar form.
  // `namePrefix` menutup selisih itu. Default kosong = perilaku lama level dokumen.
  const fullName = useMemo(() => [...castArray(namePrefix), ...castArray(name)], [namePrefix, name])
  const permissions = useMemoizePermissions({ resource: 'tags' })
  const term = useSelector(termTitleSelector)
  const websiteId = useSelector(websiteIdSelector)

  const isSpecialWebsiteId = specialWebsiteIdForTagValidation.includes(websiteId)
  const maxTagName = isSpecialWebsiteId ? 150 : 50
  const specialRegex = getSpecialRegex(isSpecialWebsiteId)

  const innerMemo = useMemo(() => {
    const newInitialValue = initialValue < 0 ? undefined : initialValue

    let _label = label
    let _placeholder = formatMessage({ id: 'financeTag.select_tag' })
    let _extraTitle = formatMessage({ id: 'financeTag.add_tag' })

    if (withTerm || !label) {
      const _term = getTerm({ formatMessage, term })
      _label = _term.tag
      _placeholder = formatMessage({ id: 'text.select' }, { title: _term.tag })
      _extraTitle = formatMessage({ id: 'button.add_with_title' }, { title: _term.tag })
    }

    const _rules = [
      ...rules,
      {
        validator: (rule, value) => {
          if (isEmpty(value)) {
            return Promise.resolve()
          }

          // eslint-disable-next-line no-restricted-syntax
          for (const _value of value) {
            if (typeof _value === 'string' && !specialRegex.test(_value)) {
              return Promise.reject(
                formatMessage({
                  id: isSpecialWebsiteId
                    ? 'financeTag.alphanumeric_and_this_regex_allowed'
                    : 'financeTag.only_alphanumeric_and_dot_allowed',
                }),
              )
            }

            if (typeof _value === 'string' && _value.length > maxTagName) {
              return Promise.reject(
                formatMessage({ id: 'financeTag.max_characters' }, { value: maxTagName }),
              )
            }
          }
          return Promise.resolve()
        },
      },
    ]

    return {
      newInitialValue,
      _label: withLabel ? _label : null,
      _placeholder,
      _extraTitle,
      rules: _rules,
    }
  }, [
    initialValue,
    label,
    formatMessage,
    withTerm,
    rules,
    withLabel,
    term,
    specialRegex,
    maxTagName,
    isSpecialWebsiteId,
  ])

  const {
    debouncedSearch,
    open,
    onDropdownVisible,
    getPopupContainer,
    onPopUpScroll,
    containerId,
    onSearch,
    popUpClassName,
    search,
  } = useSuggestion({ name, module: 'tag', uniqueId })

  const parseParams = useMemo(() => {
    return cleanBlankValue({ search: debouncedSearch, is_archive: 0, ...params })
  }, [debouncedSearch, params])

  const {
    data: tag,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteTagSuggestion(parseParams, {
    enabled: open,
  })

  const dataOptions = useMemo(() => {
    const options = []

    if (Array.isArray(defaultTag)) {
      defaultTag.forEach((row) => {
        options.push({
          id: row.id,
          name: row.name,
          color: row.color,
          ...(disableSystemReservedTags && { is_system_reserved: row.is_system_reserved }),
        })
      })
    }

    defaultTags.forEach((row) => {
      options.push({
        id: row.id,
        name: row.name,
        color: row.color,
        ...(disableSystemReservedTags && { is_system_reserved: row.is_system_reserved }),
      })
    })

    if (asFilter) {
      options.push({
        id: '',
        name: formatMessage({ id: 'financeTag.all_tags' }),
        color: null,
      })
    }

    let data = []
    const pages = tag?.pages || []
    pages.forEach((page) => {
      page.data.forEach((row) => {
        data.push(row)
      })
    })
    // Filter option is not to displayed
    if (execludeTags.length > 0) {
      data = data.filter(
        (row) =>
          !includes(
            execludeTags.map((exclude) => exclude),
            row.id,
          ),
      )
    }

    // set option from default item, usally for update view, add option, filter
    options.forEach((row) => {
      const findItem = data.find((item) => row.id === item.id)
      if (!findItem) {
        data.unshift(row)
      }
    })

    const isSearchStable = search === debouncedSearch
    // Jika mempunyai akses create tag, maka tambahkan option baru dengan id 0
    const canSuggestNewTag =
      Boolean(search) &&
      withExtra &&
      permissions.isCanAdd &&
      isSearchStable &&
      !isLoading &&
      !isFetching
    if (canSuggestNewTag) {
      const findItem = data.find((item) => toLower(item.name) === toLower(search) || item.id === 0)
      if (!findItem) {
        data.unshift({ id: 0, name: search, color: null })
      } else if (findItem.id > 0) {
        data = data.filter((row) => row.id !== findItem.id)
        data.unshift(findItem)
      }
    }

    if (!disableSystemReservedTags) {
      return data
    }

    const reservedIds = new Set()
    const collectReserved = (row) => {
      if (row?.is_system_reserved) {
        reservedIds.add(row.id)
      }
    }
    const __tags = Array.isArray(defaultTag) ? defaultTag : []
    __tags.forEach(collectReserved)
    defaultTags.forEach(collectReserved)
    data.forEach(collectReserved)

    return data.map((row) => ({
      ...row,
      disabled: row.disabled || reservedIds.has(row.id),
    }))
  }, [
    defaultTag,
    defaultTags,
    asFilter,
    tag?.pages,
    execludeTags,
    search,
    debouncedSearch,
    withExtra,
    permissions.isCanAdd,
    isLoading,
    isFetching,
    formatMessage,
    disableSystemReservedTags,
  ])

  useEffect(() => {
    if (defaultTag && !confirmLoading) {
      let newOptions = []

      if (mode === 'single') {
        if (defaultTag && defaultTag.id > 0) {
          newOptions = [defaultTag, ...newOptions]
        }
      } else if (mode === 'multiple') {
        if (Array.isArray(defaultTag)) {
          defaultTag.forEach((row) => {
            if (row && row.id > 0) {
              newOptions.unshift(row)
            }
          })
        }
      }
      setDefaultTags(newOptions)
    }
  }, [defaultTag, mode, confirmLoading])

  // Update form value setelah tag baru ditambahkan ke defaultTags
  useEffect(() => {
    if (newlyCreatedTag && form) {
      const tagInDefaultTags = defaultTags.find((_tag) => _tag.id === newlyCreatedTag.id)
      if (tagInDefaultTags) {
        const currentValue = form.getFieldValue(fullName)
        if (mode === 'single') {
          form.setFieldValue(fullName, newlyCreatedTag.id)
        } else {
          const oldTags = Array.isArray(currentValue) ? currentValue : []
          const findIndex = oldTags.findIndex((tagValue) => `${tagValue}` === '0')
          if (findIndex >= 0) {
            oldTags[findIndex] = newlyCreatedTag.id
            form.setFieldValue(fullName, oldTags)
          } else {
            form.setFieldValue(fullName, [...oldTags, newlyCreatedTag.id])
          }
        }
        setNewlyCreatedTag(null)
      }
    }
  }, [defaultTags, newlyCreatedTag, form, fullName, mode])

  const selectedHandler = useCallback(
    (value, opt) => {
      // Mencegah pemilihan saat data masih loading (initial load)
      if (isLoading || isFetching) {
        return
      }

      if (opt.id > 0) {
        // onSearch('')
        setDefaultTags((prevState) => {
          const defaults = cloneDeep(prevState)
          if (mode === 'single') {
            defaults.unshift(opt)
          } else {
            defaults.push(opt)
          }

          return defaults
        })

        if (typeof onSelect === 'function') {
          onSelect(value, dataOptions, opt, defaultTags)
        }
      } else if (withExtra && permissions.isCanAdd) {
        if (opt.name.length > maxTagName || !specialRegex.test(opt.name)) {
          const oldTags = form.getFieldValue(fullName) || []
          oldTags.splice(oldTags.indexOf(opt.id), 1)
          form.setFieldValue(fullName, oldTags)
          message.error(
            formatMessage({
              id:
                opt.name.length > maxTagName
                  ? 'financeTag.max_characters'
                  : 'financeTag.only_alphanumeric_and_dot_allowed',
              value: maxTagName,
            }),
          )
          return
        }

        // Tambahkan tag baru ke defaultTags dengan id = 0 sebagai temporary
        const _newTag = { id: 0, name: opt.name, color: null }
        setDefaultTags((prevState) => {
          const defaults = cloneDeep(prevState)
          if (mode === 'single') {
            defaults.unshift(_newTag)
          } else {
            defaults.push(_newTag)
          }
          return defaults
        })

        const removeTemporaryTag = () => {
          setDefaultTags((prevState) =>
            prevState.filter((_tag) => !(_tag.id === 0 && _tag.name === opt.name)),
          )
          const currentValue = form.getFieldValue(fullName)
          if (mode === 'single') {
            if (`${currentValue}` === '0') {
              form.setFieldValue(fullName, undefined)
            }
          } else {
            const oldTags = Array.isArray(currentValue) ? currentValue : []
            const filteredTags = oldTags.filter((tagValue) => `${tagValue}` !== '0')
            if (filteredTags.length !== oldTags.length) {
              form.setFieldValue(fullName, filteredTags)
            }
          }
        }

        // Panggil API untuk membuat tag baru
        addFinanceTag({ name: opt.name })
          .then((response) => {
            if (response.data.success) {
              const { data } = response.data

              // Replace tag temporary (id = 0) dengan data baru dari API
              setDefaultTags((prevState) => {
                const defaults = cloneDeep(prevState)
                const findIndex = defaults.findIndex(
                  (_tag) => _tag.id === 0 && _tag.name === opt.name,
                )
                if (findIndex >= 0) {
                  defaults[findIndex] = data
                  return defaults
                }
                // Jika tidak ditemukan, tambahkan saja
                if (mode === 'single') {
                  defaults.unshift(data)
                } else {
                  defaults.push(data)
                }
                return defaults
              })

              // Set state untuk trigger useEffect yang akan update form value
              setNewlyCreatedTag({ ...data, originalName: opt.name })
              // Clear search agar option dengan id = 0 tidak muncul lagi
              onSearch('')
              if (typeof onSelect === 'function') {
                onSelect(data.id, dataOptions, data, defaultTags)
              }
            } else {
              removeTemporaryTag()
              onSearch('')
              message.error(response.data.message)
            }
          })
          .catch(() => {
            removeTemporaryTag()
            onSearch('')
          })
      }
    },
    [
      isLoading,
      isFetching,
      withExtra,
      permissions.isCanAdd,
      onSelect,
      mode,
      dataOptions,
      defaultTags,
      maxTagName,
      specialRegex,
      addFinanceTag,
      form,
      fullName,
      formatMessage,
      onSearch,
    ],
  )

  const popUpScrollHandler = useCallback(
    (e) => {
      if (infiniteScroll) {
        onPopUpScroll(e, { hasNextPage, isFetchingNextPage, fetchNextPage })
      }
    },
    [fetchNextPage, hasNextPage, infiniteScroll, isFetchingNextPage, onPopUpScroll],
  )

  const clearHandler = useCallback(() => {
    setDefaultTags([])
    if (typeof onClear === 'function') {
      onClear()
    }
  }, [onClear])

  const deselectHandler = useCallback(
    (value) => {
      setDefaultTags((prevState) => {
        const _tags = prevState.filter((row) => row.id !== value)
        if (isEmpty(_tags)) {
          onSearch('')
        }
        return _tags
      })
      if (typeof onDeselect === 'function') {
        onDeselect(value)
      }
      return null
    },
    [onDeselect, onSearch],
  )

  const extraClickHandler = useCallback(
    (e) => {
      e.preventDefault()
      onToggleTagModal()
    },
    [onToggleTagModal],
  )

  const afterSavedHandler = useCallback(
    (resData) => {
      if (resData.id) {
        setDefaultTags((prevState) => {
          const _prevState = cloneDeep(prevState)
          const _data = {
            id: resData.id,
            name: resData.name,
            color: resData.color,
            owner_id: resData.owner_id,
          }
          if (mode === 'single') {
            _prevState.unshift(_data)
          } else {
            _prevState.push(_data)
          }
          return _prevState
        })
        if (typeof onAfterSaved === 'function') {
          onAfterSaved(resData)
        }
      }
    },
    [mode, onAfterSaved],
  )

  const submitHandler = useCallback(() => {
    onSubmitTag(afterSavedHandler)
  }, [afterSavedHandler, onSubmitTag])

  const tagRender = useCallback(
    ({ label: tagLabel, value, onClose, closable }) => {
      const onPreventMouseDown = (event) => {
        event.preventDefault()
        event.stopPropagation()
      }
      const _tag = dataOptions.find((item) => item.id === value)
      const backgroundColor = hexToRGB(_tag?.color ?? '#fafafa')
      const color =
        checkContrast(backgroundColor, 'rgb(255, 255, 255)') >= 4.5
          ? 'rgb(255, 255, 255)'
          : 'rgb(0, 0, 0)'

      const isReserved =
        disableSystemReservedTags && (!!_tag?.is_system_reserved || !!_tag?.disabled)
      const isClosable = closable && !isReserved

      return (
        <Tag
          color={backgroundColor}
          onMouseDown={onPreventMouseDown}
          closable={isClosable}
          onClose={onClose}
          style={{ '--color': color }}
          className={styles.tag}
        >
          {tagLabel}
        </Tag>
      )
    },
    [dataOptions, disableSystemReservedTags],
  )

  const hasReservedSelected = useMemo(() => {
    if (!disableSystemReservedTags) {
      return false
    }
    const sources = [...(Array.isArray(defaultTag) ? defaultTag : []), ...defaultTags]
    return sources.some((row) => row?.is_system_reserved)
  }, [disableSystemReservedTags, defaultTag, defaultTags])

  const optionRender = useCallback(
    ({ data }) => {
      // Jika id = 0, berarti tag baru - render custom
      if (data.id === 0) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#1890ff', fontSize: '16px', fontWeight: 'bold' }}>+</span>
            <span style={{ fontWeight: 500, color: '#1890ff' }}>
              {formatMessage({ id: 'adding_tag' })}:{' '}
              <strong style={{ color: '#333333' }}>{data.name}</strong>
            </span>
          </div>
        )
      }

      // Untuk tag yang sudah ada, tambahkan lingkaran kecil dengan warna dari data
      const backgroundColor = hexToRGB(data?.color ?? '#fafafa')
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor,
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          <span>{data.name}</span>
        </div>
      )
    },
    [formatMessage],
  )

  return (
    <>
      <div className="tag-suggestion-container" id={containerId}>
        <Form.Item
          tooltip={tooltip}
          className={className}
          label={innerMemo._label}
          style={styleFormItem}
          initialValue={innerMemo.newInitialValue}
          rules={innerMemo.rules}
          name={name}
          isListField={isListField}
        >
          <SearchInput
            placeholder={innerMemo._placeholder}
            extraTitle={innerMemo._extraTitle}
            withExtra={permissions.isCanAdd && withExtra}
            mode={mode}
            fieldNames={{ label: 'name', value: 'id' }}
            options={dataOptions}
            // searchValue={search}
            {...props}
            onSearch={onSearch}
            onSelect={selectedHandler}
            onExtraClick={extraClickHandler}
            onPopupScroll={popUpScrollHandler}
            loading={isLoading}
            infiniteScroll
            setEmptySearchOnFocus
            infiniteData={{ loading: isLoading || isFetchingNextPage }}
            onOpenChange={onDropdownVisible}
            allowClear={allowClear && !hasReservedSelected}
            ref={innerRef}
            getPopupContainer={getPopupContainer}
            classNames={{
              popup: {
                root: popUpClassName,
              },
            }}
            open={open}
            onClear={clearHandler}
            tagRender={tagRender}
            optionRender={optionRender}
            onDeselect={deselectHandler}
            defaultActiveFirstOption={
              withExtra &&
              permissions.isCanAdd &&
              !isLoading &&
              !isFetching &&
              search === debouncedSearch
            }
            autoClearSearchValue
          />
        </Form.Item>
      </div>
      {permissions.isCanAdd && (
        <AddFinanceTag
          show={showTagModal}
          ref={tagFormRef}
          confirmLoading={loadingTag}
          submitHandler={submitHandler}
          hideHandler={onToggleTagModal}
        />
      )}
    </>
  )
}

const Component = withAddTag(TagSuggestion)

export default forwardRef((props, ref) => <Component innerRef={ref} {...props} />)
